'use client';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { Button } from '@shared/components/ui/button';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { MultiSelect } from '@shared/components/ui/MultiSelect';
import { cn } from '@shared/utils/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useImportHistoryFilters } from '../../hooks/useImportHistoryFilters';

interface ImportHistoryFiltersPanelProps {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
  className?: string;
}

const FilterSection = ({ children }: { children: React.ReactNode }) => <div className="space-y-3">{children}</div>;

const FilterField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);

export const ImportHistoryFiltersPanel = ({
  initialConditions = [],
  onChange,
  className,
}: ImportHistoryFiltersPanelProps) => {
  const { t } = useTranslation('shopsModule');

  const { filters, handleStatusChange, handleShopIdChange, handleImportTypeChange, clearAllFilters } =
    useImportHistoryFilters({ initialConditions, onChange });

  const statusOptions = [
    { value: '', label: t('filters.all_statuses', 'Todos') },
    { value: 'imported', label: t('importHistory.status.imported', 'Importado') },
    { value: 'duplicated', label: t('importHistory.status.duplicated', 'Duplicado') },
    { value: 'skipped', label: t('importHistory.status.skipped', 'Omitido') },
    { value: 'failed', label: t('importHistory.status.failed', 'Fallido') },
  ];

  const importTypeOptions = [
    { value: '', label: t('filters.all_statuses', 'Todos') },
    { value: 'automatic', label: t('importHistory.importType.automatic', 'Automático') },
    { value: 'manual', label: t('importHistory.importType.manual', 'Manual') },
  ];

  // TODO: Obtener shops desde API si es necesario
  const shopOptions: { value: string; label: string }[] = [];

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.status) count++;
    if (filters.shop_id.length > 0) count++;
    if (filters.import_type) count++;
    return count;
  }, [filters]);

  return (
    <div className={cn('space-y-5', className)}>
      <FilterSection>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('importHistory.filters.status', 'Estado')}>
            <Combobox
              options={statusOptions}
              value={filters.status}
              setValue={handleStatusChange}
              placeholder={t('filters.all_statuses', 'Todos')}
              dataTestId="filter-status"
              buttonClassName="h-[38px]"
            />
          </FilterField>

          <FilterField label={t('importHistory.filters.importType', 'Tipo de importación')}>
            <Combobox
              options={importTypeOptions}
              value={filters.import_type}
              setValue={handleImportTypeChange}
              placeholder={t('filters.all_statuses', 'Todos')}
              dataTestId="filter-import-type"
              buttonClassName="h-[38px]"
            />
          </FilterField>

          {shopOptions.length > 0 && (
            <FilterField label={t('importHistory.filters.shop', 'Tienda')}>
              <MultiSelect
                options={shopOptions}
                value={filters.shop_id}
                onValueChange={handleShopIdChange}
                placeholder={t('filters.all_statuses', 'Todos')}
                emptyText={t('filters.notFound', 'Sin resultados')}
                dataTestId="filter-shop"
                triggerClassName="h-[38px]"
              />
            </FilterField>
          )}
        </div>
      </FilterSection>

      {activeFiltersCount > 0 && (
        <div className="flex items-center justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={clearAllFilters}
            type="button"
            data-testid="clear-all-filters"
          >
            Limpiar {activeFiltersCount} {activeFiltersCount === 1 ? 'filtro' : 'filtros'}
          </Button>
        </div>
      )}
    </div>
  );
};
